import { createContext } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import type {
  FormField,
  RawFormFillerData,
  FormRole,
  ConditionalFieldDefinition,
  FormAnswers,
  TandemFormProperties,
} from 'shared-utils/types/formFiller';
import { AxiosInstance } from 'axios';
import { Schema } from '@pdfme/common';

/**
 * Configuration for each checkbox feature type.
 * Add new feature types here as needed, e.g.:
 * hipaaConsent: { requiresWitness: boolean };
 */
export type CheckboxFeatureConfig = {
  providerAttestedPatientConsent: {
    drugName: string | null;
  };
};

export type CheckboxFeatureKey = keyof CheckboxFeatureConfig;

/**
 * Map of enabled checkbox features. A feature is enabled if it exists in this map.
 */
export type CheckboxFeaturesMap = {
  [K in CheckboxFeatureKey]?: CheckboxFeatureConfig[K];
};

/**
 * Data submitted for checkbox features - maps feature key to boolean value.
 */
export type CheckboxSubmitData = {
  [K in CheckboxFeatureKey]?: boolean;
};

export type LoadedInitialData = {
  status: 'success';
  formFields: FormField[];
  initialAnswers: RawFormFillerData;
  basePdfUrl: string;
  allowSignatureUpload: boolean;
  conditionalFields: ConditionalFieldDefinition[];
  roleCompletedAt: string | null;
  formName: string;
  // Has the current party completed their portion?
  hasRoleCompleted: boolean;
  // Have both parties completed their portion?
  isDocumentCompleted: boolean;
};

export type InitialData =
  | LoadedInitialData
  | {
      status: 'error';
    }
  | {
      status: 'loading';
    };

type AnswersInfo = {
  answers: FormAnswers;
  completed_at: string | null;
  signature_image_url: string | null;
};

export type FormFillerInitialDataResponse = {
  patient_answers: AnswersInfo;
  prescriber_answers: AnswersInfo;
};

export interface FormFillerContextValue {
  // These generally do not change once loaded
  initialData: InitialData;
  role: FormRole;
  token: string;
  // URLs containing the signatures for each party as an image
  signatureImageUrls: {
    patient: string | null;
    prescriber: string | null;
  };
  // Checkbox features configuration
  checkboxFeatures: CheckboxFeaturesMap;

  // Actions
  uploadSignature: UseMutationResult<{ previewUrl: string; s3Key: string }, Error, File>;
  setSignature: (data: { previewUrl: string; s3Key: string } | null) => void;

  handleSubmit: (data: RawFormFillerData, checkboxData?: CheckboxSubmitData) => Promise<void>;
  handleSave: (data: RawFormFillerData) => Promise<void>;

  shouldShowSuccessScreen: boolean;
  setShouldShowSuccessScreen: (shouldShowSuccessScreen: boolean) => void;

  getBasePdfDownloadUrl: (token: string) => string;
  getCompletedDocumentUrl: (token: string, attachment: boolean) => string;
}

export const FormFillerContext = createContext<FormFillerContextValue | null>(null);

export interface FormFillerProviderProps {
  children: React.ReactNode;
  formName: string;
  allowSignatureUpload: boolean;
  client: AxiosInstance;
  token: string;
  role: FormRole;
}

export type FormInfoResponse = {
  supports_provider_attestation?: boolean;
  drug?: {
    brand_name: string;
  };
};

export type FormFillerApi = {
  setFormAnswers: (args: {
    token: string;
    answers: FormAnswers;
    signatureImageS3Key: string | null;
  }) => Promise<{ status: string }>;
  uploadSignature: (file: File, token: string) => Promise<{ s3Key: string; previewUrl: string }>;
  completeForm: (args: {
    token: string;
    answers: FormAnswers;
    signatureImageS3Key: string | null;
    providerAttestedPatientConsent?: boolean;
  }) => Promise<{ status: string }>;
  getFormFillerTemplateJson: (token: string) => Promise<{
    template: {
      schemas: Array<Array<Schema & { tandem: TandemFormProperties }>>;
    };
    conditionalFields: ConditionalFieldDefinition[];
  }>;
  getFormFillerAnswers: (token: string) => Promise<FormFillerInitialDataResponse>;
  getFormFillerBasePdfUrl: (token: string) => string;
  getBasePdfDownloadUrl: (token: string) => string;
  getCompletedDocumentUrl: (token: string, attachment: boolean) => string;
  getFormInfo?: (token: string) => Promise<FormInfoResponse>;
};
